import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import supabase from '../utils/supabaseClient';
import { trackFormSubmission, trackEvent } from '../utils/analytics';
import './Auth.css';

const sitekey = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

function Auth() {
  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const hcaptchaRef = useRef();
  const location = useLocation();
  const urlRedirect = new URLSearchParams(location.search).get('redirect');
  const localStorageRedirect = localStorage.getItem('auth_redirect_target');
  const redirect = urlRedirect || localStorageRedirect || '/user-profile';

  const getRedirectUrl = () => {
    // Must match the origin the user is actually on (CRA :3000, Netlify Dev :8888, prod).
    const base = window.location.origin;
    return `${base}/auth/callback?redirect=${encodeURIComponent(redirect)}`;
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setSuccess('');
      setGoogleLoading(true);
      const redirectTo = getRedirectUrl();
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (oauthError) throw oauthError;
    } catch (err) {
      setError('Google sign-in failed.' + (err && err.message ? ' ' + err.message : ''));
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!email) {
      setError('Please enter your email.');
      trackFormSubmission('auth_form', false);
      trackEvent('auth_form_error', { reason: 'missing_email' });
      return;
    }
    if (!captchaToken) {
      setError('Please complete the hCaptcha.');
      trackFormSubmission('auth_form', false);
      trackEvent('auth_form_error', { reason: 'hcaptcha_failed' });
      return;
    }
    setLoading(true);
    try {
      const { error: supabaseError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: getRedirectUrl(),
        },
      });
      if (supabaseError) throw supabaseError;
      setSuccess('Magic link sent. Check your email.');
      trackFormSubmission('auth_form', true);
      trackEvent('auth_form_success', { email });
    } catch (err) {
      setError('Failed to send magic link.' + (err && err.message ? ' ' + err.message : ''));
      trackFormSubmission('auth_form', false);
      trackEvent('auth_form_error', {
        reason: 'supabase_error',
        message: err && err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__panel">
        <p className="auth-page__brand">BagpipeBlasphemy</p>
        <h1 className="auth-page__headline">Sign in to practice</h1>
        <p className="auth-page__support">
          Free — no credit card. We&apos;ll email a magic link; no password.
        </p>

        <form className="auth-page__form" onSubmit={handleSubmit}>
          <label className="auth-page__label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            className="auth-page__input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus
            autoComplete="email"
          />

          <div className="auth-page__captcha">
            <HCaptcha
              sitekey={sitekey}
              theme="dark"
              onVerify={setCaptchaToken}
              ref={hcaptchaRef}
            />
          </div>

          <button className="auth-page__submit" type="submit" disabled={loading}>
            {loading ? 'Sending…' : 'Email me a magic link'}
          </button>

          {error && (
            <p className="auth-page__message auth-page__message--error" role="alert">
              {error}
            </p>
          )}
          {success && (
            <p className="auth-page__message auth-page__message--success" role="status">
              {success}
            </p>
          )}
        </form>

        <div className="auth-page__divider">or continue with</div>

        <button
          type="button"
          className="auth-page__google"
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          aria-label="Sign in with Google"
        >
          <span className="auth-page__google-inner">
            <span className="auth-page__google-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" focusable="false">
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
                <path fill="none" d="M0 0h48v48H0z" />
              </svg>
            </span>
            <span className="auth-page__google-label">
              {googleLoading ? 'Connecting…' : 'Google'}
            </span>
          </span>
        </button>

        <p className="auth-page__footnote">
          New here? After sign-in, start with{' '}
          <Link to="/chantersync">Manage Chanters</Link>.
        </p>
      </div>
    </div>
  );
}

export default Auth;
